const JustificationTemplateListPage = lazy(() =>
    import(
        /* webpackChunkName: "JustificationTemplateListPage" */ "@project/presentation/pages/justification-template/list/justification-template-list.page"
    ).then((module) => ({ default: module.JustificationTemplateListPage })),
);
const JustificationTemplateCreatePage = lazy(() =>
    import(
        /* webpackChunkName: "JustificationTemplateCreatePage" */ "@project/presentation/pages/justification-template/create/justification-template-create.page"
    ).then((module) => ({ default: module.JustificationTemplateCreatePage })),
);
const JustificationTemplateEditPage = lazy(() =>
    import(
        /* webpackChunkName: "JustificationTemplateEditPage" */ "@project/presentation/pages/justification-template/edit/justification-template-edit.page"
    ).then((module) => ({ default: module.JustificationTemplateEditPage })),
);
const JustificationTemplateWizardPage = lazy(() =>
    import(
        /* webpackChunkName: "JustificationTemplateWizardPage" */ "@project/presentation/pages/justification-template/wizard/justification-template-wizard.page"
    ).then((module) => ({ default: module.JustificationTemplateWizardPage })),
);
const AdminCreateFinancialEntityBudgetPage = lazy(() =>
    import(
        /* webpackChunkName: "AdminCreateFinancialEntityBudgetPage" */ "@project/presentation/pages/financial-entity/budget/create/create-budget.page"
    ).then((module) => ({ default: module.CreateFinancialEntityBudgetPage })),
);
const AdminEditFinancialEntityBudgetPage = lazy(() =>
    import(
        /* webpackChunkName: "AdminEditFinancialEntityBudgetPage" */ "@project/presentation/pages/financial-entity/budget/edit/edit-budget.page"
    ).then((module) => ({ default: module.EditFinancialEntityBudgetPage })),
);
const AdminFinancialSourcesPage = lazy(() =>
    import(
        /* webpackChunkName: "AdminFinancialSourcesPage" */ "@project/presentation/pages/financial-entity/list/financial-entity-list.page"
    ).then((module) => ({ default: module.FinancialEntityListPage })),
);
const AdminCreateFinancialSourcePage = lazy(() =>
    import(
        /* webpackChunkName: "AdminCreateFinancialSourcePage" */ "@project/presentation/pages/financial-entity/create/create-financial-entity.page"
    ).then((module) => ({ default: module.CreateFinancialEntityPage })),
);
const AdminEditFinancialSourcePage = lazy(() =>
    import(
        /* webpackChunkName: "AdminEditFinancialSourcePage" */ "@project/presentation/pages/financial-entity/edit/edit-financial-entity.page"
    ).then((module) => ({ default: module.EditFinancialEntityPage })),
);
const AdminDetailFinancialSourcePage = lazy(() =>
    import(
        /* webpackChunkName: "AdminDetailFinancialSourcePage" */ "@project/presentation/pages/financial-entity/detail/detail-financial-entity.page"
    ).then((module) => ({ default: module.DetailFinancialEntityPage })),
);
import { RequireRoleGuard } from "@core/presentacion/component/routing/require-role/require-role.guard";
import { UserRoleEnum } from "@user/domain/models/user-role.model";
import { lazy } from "react";
import { Route } from "react-router-dom";

const PUBLIC_API_ENABLED = process.env.PUBLIC_API_ENABLED === "true";

const AdminUserManagementPage = lazy(() =>
    import(
        /* webpackChunkName: "AdminUserManagementPage" */ "@admin/presentation/pages/user-management/admin-user-management.page"
    ).then((module) => ({ default: module.AdminUserManagementPage })),
);

const CreateUserPage = lazy(() =>
    import(
        /* webpackChunkName: "CreateUserPage" */ "@user/presentation/pages/create/create-user.page"
    ).then((module) => ({ default: module.CreateUserPage })),
);

const EditUserPage = lazy(() =>
    import(
        /* webpackChunkName: "EditUserPage" */ "@user/presentation/pages/edit/edit-user.page"
    ).then((module) => ({ default: module.EditUserPage })),
);

const DetailUserPage = lazy(() =>
    import(
        /* webpackChunkName: "DetailUserPage" */ "@user/presentation/pages/detail/detail-user.page"
    ).then((module) => ({ default: module.DetailUserPage })),
);

const EntityApiKeysListPage = lazy(() =>
    import(
        /* webpackChunkName: "EntityApiKeysListPage" */ "@entity-api-keys/presentation/pages/list/entity-api-keys-list.page"
    ).then((module) => ({ default: module.EntityApiKeysListPage })),
);

const CreateEntityApiKeyPage = lazy(() =>
    import(
        /* webpackChunkName: "CreateEntityApiKeyPage" */ "@entity-api-keys/presentation/pages/create/create-entity-api-key.page"
    ).then((module) => ({ default: module.CreateEntityApiKeyPage })),
);

const DetailEntityApiKeyPage = lazy(() =>
    import(
        /* webpackChunkName: "DetailEntityApiKeyPage" */ "@entity-api-keys/presentation/pages/detail/detail-entity-api-key.page"
    ).then((module) => ({ default: module.DetailEntityApiKeyPage })),
);

const EditEntityApiKeyPage = lazy(() =>
    import(
        /* webpackChunkName: "EditEntityApiKeyPage" */ "@entity-api-keys/presentation/pages/edit/edit-entity-api-key.page"
    ).then((module) => ({ default: module.EditEntityApiKeyPage })),
);

const PublicApiUsageStatsPage = lazy(() =>
    import(
        /* webpackChunkName: "PublicApiUsageStatsPage" */ "@entity-api-keys/presentation/pages/usage-stats/public-api-usage-stats.page"
    ).then((module) => ({ default: module.PublicApiUsageStatsPage })),
);

export class AdminPaths {
    static readonly Root = "admin";
    static readonly Dashboard = "";
    static readonly UserManagement = "users";
    static readonly UserCreate = "users/create";
    static readonly UserEditParam = ":userId";
    static readonly UserEdit = `users/edit/${AdminPaths.UserEditParam}`;
    static readonly UserDetailParam = ":userId";
    static readonly UserDetail = `users/detail/${AdminPaths.UserDetailParam}`;
    static readonly FinancialSources = "financial-sources";
    static readonly CreateFinancialSource = "financial-sources/create";
    static readonly EditFinancialSourceParam = ":financialEntityId";
    static readonly EditFinancialSource = `financial-sources/edit/${AdminPaths.EditFinancialSourceParam}`;
    static readonly DetailFinancialSourceParam = ":financialEntityId";
    static readonly DetailFinancialSource = `financial-sources/detail/${AdminPaths.DetailFinancialSourceParam}`;
    static readonly CreateFinancialEntityBudget = `financial-sources/detail/${AdminPaths.DetailFinancialSourceParam}/budget/create`;
    static readonly EditFinancialEntityBudgetParam = ":budgetId";
    static readonly EditFinancialEntityBudget = `financial-sources/detail/${AdminPaths.DetailFinancialSourceParam}/budget/edit/${AdminPaths.EditFinancialEntityBudgetParam}`;
    static readonly EntityManagement = "entities";
    static readonly SystemSettings = "settings";
    static readonly Reports = "reports";
    static readonly TemplateManagement = "templates";
    static readonly TemplateJustificationList = "templates/justification";
    static readonly TemplateJustificationCreate =
        "templates/justification/create";
    static readonly TemplateJustificationWizard =
        "templates/justification/wizard";
    static readonly TemplateJustificationEditParam = ":templateId";
    static readonly TemplateJustificationEdit = `templates/justification/edit/${AdminPaths.TemplateJustificationEditParam}`;

    static readonly EntityApiKeys = "entity-api-keys";
    static readonly EntityApiKeyCreate = "entity-api-keys/create";
    static readonly EntityApiKeyEditParam = ":apiKeyId";
    static readonly EntityApiKeyEdit = `entity-api-keys/edit/${AdminPaths.EntityApiKeyEditParam}`;
    static readonly EntityApiKeyDetailParam = ":apiKeyId";
    static readonly EntityApiKeyDetail = `entity-api-keys/detail/${AdminPaths.EntityApiKeyDetailParam}`;

    static readonly PublicApiUsageStats = "public-api-usage-stats";
}

export class AdminRoutes {
    static readonly ROOT = `/${AdminPaths.Root}`;
    static readonly DASHBOARD = `/${AdminPaths.Root}`;
    static readonly FINANCIAL_SOURCES = `/${AdminPaths.Root}/${AdminPaths.FinancialSources}`;
    static readonly CREATE_FINANCIAL_SOURCE = `/${AdminPaths.Root}/${AdminPaths.CreateFinancialSource}`;
    static readonly EDIT_FINANCIAL_SOURCE = (
        financialEntityId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.EditFinancialSource.replace(
            AdminPaths.EditFinancialSourceParam,
            financialEntityId.toString(),
        )}`;
    static readonly DETAIL_FINANCIAL_SOURCE = (
        financialEntityId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.DetailFinancialSource.replace(
            AdminPaths.DetailFinancialSourceParam,
            financialEntityId.toString(),
        )}`;
    static readonly CREATE_FINANCIAL_ENTITY_BUDGET = (
        financialEntityId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.CreateFinancialEntityBudget.replace(
            AdminPaths.DetailFinancialSourceParam,
            financialEntityId.toString(),
        )}`;
    static readonly EDIT_FINANCIAL_ENTITY_BUDGET = (
        financialEntityId: number | string,
        budgetId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.EditFinancialEntityBudget.replace(
            AdminPaths.DetailFinancialSourceParam,
            financialEntityId.toString(),
        ).replace(
            AdminPaths.EditFinancialEntityBudgetParam,
            budgetId.toString(),
        )}`;
    static readonly USER_MANAGEMENT = `/${AdminPaths.Root}/${AdminPaths.UserManagement}`;
    static readonly USER_CREATE = `/${AdminPaths.Root}/${AdminPaths.UserCreate}`;
    static readonly USER_EDIT = (userId: number): string =>
        `/${AdminPaths.Root}/${AdminPaths.UserEdit.replace(
            AdminPaths.UserEditParam,
            userId.toString(),
        )}`;
    static readonly USER_DETAIL = (userId: number): string =>
        `/${AdminPaths.Root}/${AdminPaths.UserDetail.replace(
            AdminPaths.UserDetailParam,
            userId.toString(),
        )}`;
    static readonly ENTITY_MANAGEMENT = `/${AdminPaths.Root}/${AdminPaths.EntityManagement}`;
    static readonly SYSTEM_SETTINGS = `/${AdminPaths.Root}/${AdminPaths.SystemSettings}`;
    static readonly REPORTS = `/${AdminPaths.Root}/${AdminPaths.Reports}`;
    static readonly TEMPLATE_MANAGEMENT = `/${AdminPaths.Root}/${AdminPaths.TemplateManagement}`;
    static readonly TEMPLATE_JUSTIFICATION_LIST = `/${AdminPaths.Root}/${AdminPaths.TemplateJustificationList}`;
    static readonly TEMPLATE_JUSTIFICATION_CREATE = `/${AdminPaths.Root}/${AdminPaths.TemplateJustificationCreate}`;
    static readonly TEMPLATE_JUSTIFICATION_WIZARD = `/${AdminPaths.Root}/${AdminPaths.TemplateJustificationWizard}`;
    static readonly TEMPLATE_JUSTIFICATION_EDIT = (
        templateId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.TemplateJustificationEdit.replace(
            AdminPaths.TemplateJustificationEditParam,
            templateId.toString(),
        )}`;

    static readonly ENTITY_API_KEYS_LIST = `/${AdminPaths.Root}/${AdminPaths.EntityApiKeys}`;
    static readonly ENTITY_API_KEYS_CREATE = `/${AdminPaths.Root}/${AdminPaths.EntityApiKeyCreate}`;
    static readonly ENTITY_API_KEYS_EDIT = (
        apiKeyId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.EntityApiKeyEdit.replace(
            AdminPaths.EntityApiKeyEditParam,
            apiKeyId.toString(),
        )}`;
    static readonly ENTITY_API_KEYS_DETAIL = (
        apiKeyId: number | string,
    ): string =>
        `/${AdminPaths.Root}/${AdminPaths.EntityApiKeyDetail.replace(
            AdminPaths.EntityApiKeyDetailParam,
            apiKeyId.toString(),
        )}`;

    static readonly PUBLIC_API_USAGE_STATS = `/${AdminPaths.Root}/${AdminPaths.PublicApiUsageStats}`;
}

export const adminRoutes = (
    <Route path={AdminPaths.Root}>
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminCreateFinancialSourcePage />
                </RequireRoleGuard>
            }
            path={AdminPaths.CreateFinancialSource}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminFinancialSourcesPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.FinancialSources}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <JustificationTemplateListPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.TemplateJustificationList}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <JustificationTemplateCreatePage />
                </RequireRoleGuard>
            }
            path={AdminPaths.TemplateJustificationCreate}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <JustificationTemplateWizardPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.TemplateJustificationWizard}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <JustificationTemplateEditPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.TemplateJustificationEdit}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminEditFinancialSourcePage />
                </RequireRoleGuard>
            }
            path={AdminPaths.EditFinancialSource}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminDetailFinancialSourcePage />
                </RequireRoleGuard>
            }
            path={AdminPaths.DetailFinancialSource}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminCreateFinancialEntityBudgetPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.CreateFinancialEntityBudget}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminEditFinancialEntityBudgetPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.EditFinancialEntityBudget}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <AdminUserManagementPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.UserManagement}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <CreateUserPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.UserCreate}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <EditUserPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.UserEdit}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                >
                    <DetailUserPage />
                </RequireRoleGuard>
            }
            path={AdminPaths.UserDetail}
        />

        {PUBLIC_API_ENABLED && (
            <>
                <Route
                    element={
                        <RequireRoleGuard
                            rolesIn={[
                                UserRoleEnum.SuperAdmin,
                                UserRoleEnum.Admin,
                            ]}
                        >
                            <EntityApiKeysListPage />
                        </RequireRoleGuard>
                    }
                    path={AdminPaths.EntityApiKeys}
                />
                <Route
                    element={
                        <RequireRoleGuard
                            rolesIn={[
                                UserRoleEnum.SuperAdmin,
                                UserRoleEnum.Admin,
                            ]}
                        >
                            <CreateEntityApiKeyPage />
                        </RequireRoleGuard>
                    }
                    path={AdminPaths.EntityApiKeyCreate}
                />
                <Route
                    element={
                        <RequireRoleGuard
                            rolesIn={[
                                UserRoleEnum.SuperAdmin,
                                UserRoleEnum.Admin,
                            ]}
                        >
                            <EditEntityApiKeyPage />
                        </RequireRoleGuard>
                    }
                    path={AdminPaths.EntityApiKeyEdit}
                />
                <Route
                    element={
                        <RequireRoleGuard
                            rolesIn={[
                                UserRoleEnum.SuperAdmin,
                                UserRoleEnum.Admin,
                            ]}
                        >
                            <DetailEntityApiKeyPage />
                        </RequireRoleGuard>
                    }
                    path={AdminPaths.EntityApiKeyDetail}
                />

                <Route
                    element={
                        <RequireRoleGuard
                            rolesIn={[
                                UserRoleEnum.SuperAdmin,
                                UserRoleEnum.Admin,
                            ]}
                        >
                            <PublicApiUsageStatsPage />
                        </RequireRoleGuard>
                    }
                    path={AdminPaths.PublicApiUsageStats}
                />
            </>
        )}
    </Route>
);
